<template>
  <PopOver
    :open="isOpen"
    :offset="offset"
    :placement="placement"
    :use-portal="usePortal"
    :middleware="middleware"
    :empty-substrate="emptySubstrate"
    :follow-target="followTarget"
  >
    <span
      ref="target"
      class="popover-menu__target"
      @click.prevent="onClickTarget"
      @mouseenter="onMouseEnterTarget"
      @mouseleave="onMouseLeaveTarget"
    >
      <slot
        name="target"
        :close-menu="maybeDelayCloseMenu"
        :is-open="isOpen"
      />
    </span>

    <template #content>
      <Card
        ref="menuContent"
        bordered
        class="popover-menu__content"
        :aria-label="`Pop over menu content ${isOpen ? 'open' : 'closed'}`"
        @mouseenter="onMouseEnterMenu"
        @mouseleave="onMouseLeaveMenu"
      >
        <slot
          name="menu"
          :close-menu="maybeDelayCloseMenu"
          :is-open="isOpen"
        />
      </Card>
    </template>
  </PopOver>
</template>

<script lang="ts" setup>
import type { OffsetOptions, Placement } from '@floating-ui/vue'
import Card from '@/uiKit/Card/Card.vue'
import PopOver from '@/uiKit/PopOver.vue'
import { computed, ref, onUnmounted } from 'vue'
import { onClickOutside } from '@vueuse/core'
import type { PopOverMiddleware } from '@/uiKit/popOver.utils'

const props = withDefaults(
  defineProps<{
    /**
     * Open state is implemented within the component, but can be overridden by passing prop `open`).
     * In this case, the state will be fully controlled by the parent component, and the internal
     * logic is ignored.
     */
    open?: boolean
    offset?: OffsetOptions
    /**
     * Accepts a list of middleware names to apply to the PopOver component
     */
    middleware?: PopOverMiddleware[]
    placement?: Placement
    /**
     * We should avoid using portal unless necessary. 99% of popup menus can be rendered alongside the
     * trigger element, with some edge cases needing a different placement or an extra style.
     */
    usePortal?: boolean
    /**
     * Add an empty substrate to the popover to prevent the click events outside the popover.
     */
    emptySubstrate?: boolean

    ignoreClickOutsideFrom?: string[]
    /**
     * Delay configuration for opening and closing the menu
     */
    delay?: {
      open: number
      close: number
    }
    /**
     * Open the menu on hover instead of click
     */
    openOnHover?: boolean
    /**
     * Whether the menu should follow the target when it moves.
     * When false, the menu will stay in its initial position.
     */
    followTarget?: boolean
  }>(),
  {
    open: undefined,
    placement: 'top-start',
    middleware: () => [],
    offset: 7,
    usePortal: false,
    ignoreClickOutsideFrom: () => [],
    delay: () => ({ open: 0, close: 0 }),
    openOnHover: false,
    followTarget: true,
  },
)

const emit = defineEmits<{
  (e: 'click:outside', mouseEvent: MouseEvent): void
  (e: 'state:change', isOpen: boolean): void
}>()

const menuContent = ref<HTMLElement | null>(null)
const target = ref<HTMLElement | null>(null)
const openInternal = ref(false)
const openTimeoutId = ref<number | null>(null)
const closeTimeoutId = ref<number | null>(null)

const isOpen = computed(() => (props.open !== undefined ? props.open : openInternal.value))

const clearAllTimeouts = (): void => {
  if (openTimeoutId.value !== null) {
    window.clearTimeout(openTimeoutId.value)
    openTimeoutId.value = null
  }
  if (closeTimeoutId.value !== null) {
    window.clearTimeout(closeTimeoutId.value)
    closeTimeoutId.value = null
  }
}

const openMenu = (): void => {
  openInternal.value = true
  emit('state:change', true)
}

const closeMenu = (): void => {
  openInternal.value = false
  emit('state:change', false)
}

const maybeDelayOpenMenu = (): void => {
  if (props.open !== undefined) {
    return
  }

  clearAllTimeouts()

  const { open: openDelay } = props.delay
  if (!openDelay || openDelay === 0) {
    openMenu()
    return
  }

  openTimeoutId.value = window.setTimeout(() => {
    openMenu()
    openTimeoutId.value = null
  }, openDelay)
}

const maybeDelayCloseMenu = (): void => {
  if (props.open !== undefined) {
    return
  }

  clearAllTimeouts()

  const { close: closeDelay } = props.delay
  if (!closeDelay || closeDelay === 0) {
    closeMenu()
    return
  }

  closeTimeoutId.value = window.setTimeout(() => {
    closeMenu()
    closeTimeoutId.value = null
  }, closeDelay)
}

const onClickTarget = (): void => {
  if (props.open !== undefined || props.openOnHover) {
    return
  }

  openInternal.value ? maybeDelayCloseMenu() : maybeDelayOpenMenu()
}

const onMouseEnterTarget = (): void => {
  if (!props.openOnHover) {
    return
  }
  maybeDelayOpenMenu()
}

const onMouseLeaveTarget = (): void => {
  if (!props.openOnHover) {
    return
  }
  maybeDelayCloseMenu()
}

const onMouseEnterMenu = (): void => {
  if (!props.openOnHover) {
    return
  }
  // Cancel close timeout when hovering over menu
  if (closeTimeoutId.value !== null) {
    window.clearTimeout(closeTimeoutId.value)
    closeTimeoutId.value = null
  }
}

const onMouseLeaveMenu = (): void => {
  if (!props.openOnHover) {
    return
  }
  maybeDelayCloseMenu()
}

onClickOutside(
  menuContent,
  (e: MouseEvent) => {
    const isClickingTrigger = e.target === target.value || target.value?.contains(e.target as Node)
    if (isClickingTrigger) {
      return
    }
    if (props.open !== undefined) {
      emit('click:outside', e)
    } else {
      maybeDelayCloseMenu()
    }
  },
  {
    ignore: [menuContent, ...props.ignoreClickOutsideFrom],
  },
)

onUnmounted(() => clearAllTimeouts())

defineExpose({
  closeMenu,
  maybeDelayCloseMenu,
  openMenu,
  maybeDelayOpenMenu,
})
</script>

<style scoped lang="scss">
@import '@/uiKit/assets/shadows.scss';
@import '@/uiKit/assets/spacings.scss';
@import '@/assets/styles/shared/scrollbarV2';

.popover-menu__content {
  box-shadow: $boxShadowSM;
  border-radius: var(--size-3);
  max-height: 90%;
  max-height: 90vh;
  @include scrollbarV2;

  // We should probably change the `CardSection` default padding, but I don't have the go-ahead yet
  // Making sure we default it only for the first children
  > :deep(.card__section) {
    padding: var(--size-1);
  }
}
</style>
